/**
 * UltraAdminMediaHealthPage
 * ─────────────────────────
 * Read-only diagnostic dashboard for S3 image integrity.
 *
 * Shows a breakdown of supplier_images records by S3 presence status
 * (healthy / half_orphan / thumb_missing / full_orphan) and lets the
 * operator run two safe repair actions:
 *   1. Half-orphan → rewrite s3_key = s3_thumb_key  (keep record, 400px)
 *   2. Full-orphan → soft-delete records             (remove dead entries)
 *
 * Both actions support Dry-Run first.
 */
import React, { useEffect, useState } from 'react';
import { apiClient } from '../lib/apiClient';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Image as ImageIcon,
  Wrench, Eye, Package,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import AdminSidebar from '../components/AdminSidebar';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';

export default function UltraAdminMediaHealthPage() {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('half_orphan');
  const [repairResult, setRepairResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/ultra-admin/media-health');
      setReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Tarama başarısız.' : 'Scan failed.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { scan(); }, []);

  const runRepair = async (apply) => {
    setBusy(true);
    setRepairResult(null);
    try {
      const r = await apiClient.post(
        `/ultra-admin/media-health/repair?apply=${apply}&mode=${mode}`
      );
      setRepairResult(r.data);
      if (apply) {
        toast.success(
          tr
            ? `Uygulandı: ${r.data.affected_count} kayıt düzeltildi.`
            : `Applied: ${r.data.affected_count} records repaired.`
        );
        await scan();
      } else {
        toast.info(
          tr
            ? `Dry-run: ${r.data.candidate_count} aday kayıt bulundu.`
            : `Dry-run: ${r.data.candidate_count} candidate records found.`
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'İşlem başarısız.' : 'Repair failed.'));
    } finally {
      setBusy(false);
    }
  };

  const statCard = (icon, label, count, color) => (
    <div className={`rounded-2xl border ${color.border} ${color.bg} p-4`}>
      <div className={`flex items-center gap-2 ${color.text} mb-1`}>
        {icon}
        <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-black text-gray-900">{count ?? '—'}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar />
      <main className="md:pl-64 px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto" data-testid="media-health-page">
        <PageIntro {...getPageIntro('media-health', language)} />

        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-indigo-600" />
            {tr ? 'Tarama Sonuçları' : 'Scan Results'}
          </h1>
          <button
            onClick={scan}
            disabled={loading}
            data-testid="media-health-rescan-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {tr ? 'Yeniden Tara' : 'Re-scan'}
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {statCard(
            <CheckCircle2 className="h-4 w-4" />,
            tr ? 'Sağlıklı' : 'Healthy',
            report?.healthy_count,
            { border: 'border-emerald-200', bg: 'bg-emerald-50/60', text: 'text-emerald-700' }
          )}
          {statCard(
            <AlertTriangle className="h-4 w-4" />,
            tr ? 'Yarı-orphan' : 'Half-orphan',
            report?.half_orphan_count,
            { border: 'border-amber-200', bg: 'bg-amber-50/60', text: 'text-amber-700' }
          )}
          {statCard(
            <ImageIcon className="h-4 w-4" />,
            tr ? 'Thumb Eksik' : 'Thumb Missing',
            report?.thumb_missing_count,
            { border: 'border-sky-200', bg: 'bg-sky-50/60', text: 'text-sky-700' }
          )}
          {statCard(
            <XCircle className="h-4 w-4" />,
            tr ? 'Tam Orphan' : 'Full-orphan',
            report?.full_orphan_count,
            { border: 'border-red-200', bg: 'bg-red-50/60', text: 'text-red-700' }
          )}
        </div>

        {report && (
          <p className="text-xs text-gray-500 mb-4">
            {tr
              ? `${report.scanned_count} kayıt tarandı · ${new Date(report.scanned_at).toLocaleString('tr-TR')}`
              : `${report.scanned_count} records scanned · ${new Date(report.scanned_at).toLocaleString()}`}
          </p>
        )}

        {/* Repair panel */}
        {report && ((report.half_orphan_count + report.full_orphan_count) > 0) && (
          <div className="rounded-2xl border border-gray-200 bg-white p-5 mb-6" data-testid="media-repair-panel">
            <h2 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
              <Wrench className="h-4 w-4 text-indigo-600" />
              {tr ? 'Onarım İşlemi' : 'Repair Action'}
            </h2>
            <p className="text-xs text-gray-500 mb-4 leading-relaxed">
              {tr
                ? 'Hangi kategoriyi onarmak istiyorsunuz? Önce "Ön İzleme (Dry-Run)" butonuna basın, listeyi kontrol edin, ardından "Kalıcı Uygula" ile onaylayın.'
                : 'Pick which category to repair. Run "Preview (Dry-Run)" first, review the list, then press "Apply Permanently" to commit.'}
            </p>
            <div className="flex gap-2 mb-3">
              {[
                { k: 'half_orphan', tr_l: 'Yarı-orphan', en_l: 'Half-orphan', count: report.half_orphan_count },
                { k: 'full_orphan', tr_l: 'Tam Orphan', en_l: 'Full-orphan', count: report.full_orphan_count },
              ].map(opt => (
                <button
                  key={opt.k}
                  onClick={() => { setMode(opt.k); setRepairResult(null); }}
                  disabled={busy || opt.count === 0}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    mode === opt.k ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } disabled:opacity-40`}
                  data-testid={`repair-mode-${opt.k}`}
                >
                  {tr ? opt.tr_l : opt.en_l} ({opt.count})
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => runRepair(false)}
                disabled={busy}
                data-testid="repair-dryrun-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-semibold disabled:opacity-60"
              >
                <Eye className="h-4 w-4" />
                {tr ? 'Ön İzleme (Dry-Run)' : 'Preview (Dry-Run)'}
              </button>
              <button
                onClick={() => {
                  if (window.confirm(tr
                    ? 'Bu işlem DB\'yi kalıcı olarak değiştirir. Devam edilsin mi?'
                    : 'This permanently modifies the DB. Continue?')) {
                    runRepair(true);
                  }
                }}
                disabled={busy}
                data-testid="repair-apply-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60"
              >
                <Wrench className="h-4 w-4" />
                {tr ? 'Kalıcı Uygula' : 'Apply Permanently'}
              </button>
            </div>
            {repairResult && (
              <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 p-3" data-testid="repair-result">
                <p className="text-xs text-gray-700 mb-2">
                  {repairResult.apply
                    ? (tr ? `Uygulandı: ${repairResult.affected_count} kayıt.` : `Applied: ${repairResult.affected_count} records.`)
                    : (tr ? `Ön izleme: ${repairResult.candidate_count} aday.` : `Dry-run: ${repairResult.candidate_count} candidates.`)}
                </p>
                {repairResult.sample?.length > 0 && (
                  <ul className="space-y-1 max-h-48 overflow-y-auto">
                    {repairResult.sample.map((s, i) => (
                      <li key={s.id} className="text-[11px] font-mono text-gray-600 truncate">
                        {i + 1}. {s.parent_type}:{s.parent_id} → {s.id}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* Affected products list */}
        {report?.affected_products?.length > 0 && (
          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Package className="h-4 w-4 text-indigo-600" />
              {tr ? 'Etkilenen Ürünler' : 'Affected Products'} ({report.affected_products.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 font-semibold">{tr ? 'Ürün Referansı' : 'Parent'}</th>
                    <th className="py-2 font-semibold text-right">{tr ? 'Yarı' : 'Half'}</th>
                    <th className="py-2 font-semibold text-right">{tr ? 'Thumb Yok' : 'Thumb Miss'}</th>
                    <th className="py-2 font-semibold text-right">{tr ? 'Tam' : 'Full'}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.affected_products.map((r, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 font-mono text-gray-700 truncate max-w-[24rem]">{r.parent}</td>
                      <td className="py-2 text-right font-bold text-amber-700">{r.half_orphan || ''}</td>
                      <td className="py-2 text-right font-bold text-sky-700">{r.thumb_missing || ''}</td>
                      <td className="py-2 text-right font-bold text-red-700">{r.full_orphan || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {report && report.healthy_count === report.scanned_count && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
            <p className="font-bold text-emerald-900">
              {tr ? 'Tüm medya dosyaları sağlıklı' : 'All media records healthy'}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
